import * as React from 'react';
import { JsonForms, JsonFormsInitStateProps } from '@jsonforms/react';
import type {
  JsonFormsCore,
  JsonFormsRendererRegistryEntry,
  Middleware,
} from '@jsonforms/core';
import { createConfigurableAjv } from '@web-config-app/validation';
import { vanillaCells } from '@jsonforms/vanilla-renderers';

export interface OnChangeParams {
  data?: JsonFormsCore['data'];
  errors?: JsonFormsCore['errors'];
}

export type EntityFormProps = Omit<
  JsonFormsInitStateProps,
  'data' | 'uischema' | 'renderers'
> & {
  data?: any;
  renderers: JsonFormsRendererRegistryEntry[];
  onDataChange?: (params: OnChangeParams['data']) => any;
  onErrorsChange?: (params: OnChangeParams['errors']) => any;
  // whether of not to show individual property validation on form inputs
  showValidation?: boolean;
  // json forms middleware https://jsonforms.io/docs/middleware/#:~:text=JSON%20Forms%20offers%20the%20option,data%20updates%20and%20validation%20methods.
  middleware?: Middleware;
  /*
   * This is useful when you want to disable all form elements in the form
   * without having to set the readonly property on each form element
   */
  readOnly?: boolean;
  /*
   * allowed URI schemes (without trailing ':'). If provided, this will create
   * a per-form AJV instance (via createConfigurableAjv)
   */
  allowedUriProtocols?: string[];
};

const defaultConfigOptions = {
  restrict: true,
  hideRequiredAsterisk: true,
};

export const EntityForm: React.FC<EntityFormProps> = ({
  schema,
  data,
  renderers = [],
  config: configOptions,
  ajv: customAjv,
  onDataChange,
  onErrorsChange,
  showValidation,
  middleware,
  readOnly,
  allowedUriProtocols,
  additionalErrors,
}) => {
  const config = React.useMemo(
    () => ({ ...defaultConfigOptions, ...configOptions }),
    [configOptions],
  );

  // to avoid the initial render from triggering the onChange callbacks
  const initialRender = React.useRef(true);

  /**
   * Cast needed because @jsonforms/core pins ajv@8.12.0 while
   * @web-config-app/validation uses ajv@8.18.0 (security fix).
   * Both are AJV v8 minors with identical runtime APIs.
   */
  const ajvInstance = React.useMemo(() => {
    if (customAjv) return customAjv;
    return createConfigurableAjv(allowedUriProtocols) as unknown as NonNullable<
      JsonFormsInitStateProps['ajv']
    >;
  }, [customAjv, allowedUriProtocols]);

  return schema ? (
    <JsonForms
      schema={schema}
      renderers={renderers}
      cells={vanillaCells}
      data={data}
      onChange={(changes) => {
        if (!initialRender.current) {
          onErrorsChange?.(changes.errors);
          onDataChange?.(changes.data);
        }
        initialRender.current = false;
      }}
      config={config}
      middleware={middleware}
      ajv={ajvInstance}
      validationMode={showValidation ? 'ValidateAndShow' : 'ValidateAndHide'}
      readonly={readOnly}
      additionalErrors={additionalErrors}
    />
  ) : null;
};
